import { unref as _unref, createVNode as _createVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, toHandlers as _toHandlers, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "fw-bold"
}
const _hoisted_2 = ["onClick"]

import FullCalendar from "@fullcalendar/vue3";
import { ref, onBeforeUnmount, computed, watch } from "vue";
import { Toaster, toast } from "vue-sonner";
import CalendarDatePicker from "./components/CalendarDatePicker.vue";
import FilterSelector from "./components/FilterSelector.vue";
import HolidayBanner from "./components/HolidayBanner.vue";
import RoomInfoPopover from "./components/RoomInfoPopover.vue";
import RoomSearchHeader from "./components/RoomSearchHeader.vue";
import { useCalendarConfig } from "./composables/useCalendarConfig";
import { useMasterDataHelpers } from "./composables/useMasterDataHelpers";
import { useCalendarFilters } from "./composables/useCalendarFilters.js";
import { useQueryClient, useQuery } from "@tanstack/vue-query";
import { useBookingActions } from "../composables/useBookingActions.js";
import {
	useAllBookingsQuery,
	useBookingsByRoomQuery,
	getConcertRequestPrefillQueryOptions,
} from "../api/queries/bookingQueries.js";
import { generateResourceIconClass } from "./utils/resourceIconUtils";
import { evaluateCalendarSelection } from "./utils/walkInSelection.js";

// Props

export default {
  __name: 'BookingCalendar',
  props: {
	roomId: { type: Number, default: null },
	roomBookingApiUrl: { type: String, required: true },
	userApiUrl: { type: String, required: true },
	overlayConcertRequestsApiUrl: { type: String, default: null },
	mode: {
		type: String,
		validator: (value) => ["single-room", "multi-room"].includes(value),
		required: true,
	},
},
  setup(__props, { expose: __expose }) {

const props = __props;

// Local state for UI only
const calendar = ref(null);

// TanStack Query setup
const queryClient = useQueryClient();
const masterData = useMasterDataHelpers();

// Setup calendar filters for calendar modes that expose filter controls
const calendarFilters = useCalendarFilters(calendar, masterData);

const activeFilterConfigs = computed(() => {
	if (!calendarFilters?.initialized.value) {
		return [];
	}

	if (props.mode === "single-room") {
		return [
			...calendarFilters.getDimOtherBookingsFilterConfigs(),
			...calendarFilters.getBookingFilterConfigs(),
		];
	}

	return [
		...calendarFilters.getRoomFilterConfigs(),
		...calendarFilters.getBookingFilterConfigs(),
	];
});

const shouldShowFilterSelector = computed(
	() => calendarFilters?.initialized.value && activeFilterConfigs.value.length > 0
);

const showInlineFilterSelector = computed(
	() => props.mode === "multi-room" && shouldShowFilterSelector.value
);

const filterSelectorProps = computed(() => ({
	filterConfigs: activeFilterConfigs.value,
	availableEquipment: calendarFilters.getAvailableEquipment(),
	availableRoomTypes: calendarFilters.getAvailableRoomTypes(),
	availableUsageTypes: calendarFilters.getAvailableUsageTypes(),
	showRoomFilters: props.mode === "multi-room",
	showDimOtherBookingsFilter: true,
}));

const filterSelectorEvents = {
	"toggle-bookable-rooms": () => calendarFilters.toggleFilter("bookable-rooms"),
	"toggle-dim-other-bookings": () => calendarFilters.toggleFilter("dim-other-bookings"),
	"update-equipment-selection": (ids) => calendarFilters.updateEquipmentSelection(ids),
	"update-room-type-selection": (ids) => calendarFilters.updateRoomTypeSelection(ids),
	"update-seat-count": (minSeats) => calendarFilters.updateSeatCountFilter(minSeats),
	"update-usage-type-selection": (id) => calendarFilters.updateUsageTypeSelection(id),
	"update-person-selection": (id, name) => calendarFilters.updatePersonSelection(id, name),
	"remove-filter": handleRemoveFilter,
	"reset-all-filters": calendarFilters.clearAllFilters,
};

const filterSelectorSlotProps = computed(() => ({
	component: FilterSelector,
	show: props.mode === "single-room" && shouldShowFilterSelector.value,
	props: filterSelectorProps.value,
	events: filterSelectorEvents,
}));

// Use booking actions from provider
const {
	createBooking,
	editBooking,
	viewBooking,
	registerCalendarRefresh,
	unregisterCalendarRefresh,
} = useBookingActions();

const prefillUrlParams = new URLSearchParams(window.location.search);
const prefillConcertRequestId = prefillUrlParams.get("prefillConcertRequestId");

if (prefillConcertRequestId) {
	queryClient
		.fetchQuery(getConcertRequestPrefillQueryOptions(prefillConcertRequestId))
		.then((prefillData) => {
			createBooking(null, prefillData);

			prefillUrlParams.delete("prefillConcertRequestId");
			const newQuery = prefillUrlParams.toString();
			const newUrl =
				window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
			window.history.replaceState({}, "", newUrl);
		})
		.catch((e) => {
			console.warn("Prefill request failed:", e);
		});
}

// Date picker state
const datePickerState = ref({
	isVisible: false,
	currentDate: new Date(),
});

let unsubscribeCalendarFilterRefresh = null;

// Room popover state
const roomPopoverState = ref({
	isOpen: false,
	roomData: null,
	triggerElement: null,
});

// Store refs to room label elements
const roomLabelRefs = ref({});

// Date picker functions
function showDatePicker() {
	// Get current calendar date
	let currentDate = new Date();
	try {
		if (calendar.value) {
			const calendarApi = calendar.value.getApi();
			const view = calendarApi.view;
			if (view && view.activeStart) {
				currentDate = view.activeStart;
			}
		}
	} catch (error) {
		console.warn("Could not get current calendar date, using today:", error);
	}

	datePickerState.value = {
		isVisible: true,
		currentDate: currentDate,
	};
}

function closeDatePicker() {
	datePickerState.value.isVisible = false;
}

function handleDateSelected(dateString) {
	if (calendar.value) {
		const calendarApi = calendar.value.getApi();
		calendarApi.gotoDate(dateString);
	}
}

// Room search handler
function handleRoomSearch(query) {
	if (calendarFilters) {
		calendarFilters.updateRoomSearch(query);
	}
}

// Room popover functions
function setRoomLabelRef(roomId, el) {
	if (el) {
		roomLabelRefs.value[roomId] = el;
	}
}

function showRoomInfoPopover(roomResource) {
	const triggerElement = roomLabelRefs.value[roomResource.id];
	if (triggerElement) {
		roomPopoverState.value = {
			isOpen: true,
			roomData: roomResource,
			triggerElement: triggerElement,
		};
	}
}

function closeRoomInfoPopover() {
	roomPopoverState.value.isOpen = false;
}

// Calendar handlers
const handlers = {
	handleEventAdd,
	handleEventRemove,
	handleDateSelect,
	handleEventClick,
	handleDatesChange,
	handleViewDidMount,
	handleEventDrop,
	handleEventResize,
	showDatePicker,
};

// Use calendar configuration composable with queryClient
const { calendarOptions, getSpecialFunctions } = useCalendarConfig(
	props,
	handlers,
	queryClient,
	{
		mode: props.mode,
		masterData,
		calendarFilters,
	}
);

// FullCalendar View Lifecycle
function handleViewDidMount() {
	// Setup calendar filter refresh after calendar is mounted
	if (calendarFilters) {
		unsubscribeCalendarFilterRefresh?.();
		const specialFunctions = getSpecialFunctions();
		unsubscribeCalendarFilterRefresh = calendarFilters.setupCalendarRefresh(
			specialFunctions.saveExpansionStateBeforeRefetch,
			{ refreshResources: props.mode === "multi-room" }
		);
	}
}

// FullCalendar Event Lifecycle callbacks (for programmatic changes)
function handleEventAdd() {}
function handleEventRemove() {}

// User interaction handlers
function handleDateSelect(selectInfo) {
	const roomId = parseInt(selectInfo.resource?.id) || props.roomId;

	const { allowed, isWalkIn } = evaluateCalendarSelection({
		selectStart: selectInfo.start,
		selectEnd: selectInfo.end,
		userRole: masterData.userRole.value,
		earliestBookingSeconds: masterData.earliestBookingSeconds?.value || 900,
	});

	if (!allowed) {
		selectInfo.view.calendar.unselect();
		return;
	}

	const canCreate = isWalkIn
		? masterData.canCreateWalkInInRoom.value(roomId)
		: masterData.canCreateInRoom.value(roomId);
	if (!canCreate) {
		selectInfo.view.calendar.unselect();
		return;
	}

	// Validate minimum duration (always require minimum 15 minutes)
	const duration = selectInfo.end - selectInfo.start;
	if (duration < 15 * 60 * 1000) {
		alert("Buchung muss mindestens 15 Minuten lang sein.");
		selectInfo.view.calendar.unselect();
		return;
	}

	// Use booking actions to create new booking
	createBooking(
		{
			start: selectInfo.start,
			end: selectInfo.end,
			roomId: roomId,
		},
		null,
		isWalkIn
	);

	// Clear selection
	selectInfo.view.calendar.unselect();
}

function handleEventClick(clickInfo) {
	const event = clickInfo.event;
	const booking = event.extendedProps.booking;

	if (event.extendedProps?.type === "concert-request") {
		clickInfo.jsEvent.preventDefault();

		const concertRequest = event.extendedProps.concertRequest;
		if (concertRequest?.canEdit) {
			window.location.href = event.url;
		} else if (concertRequest?.isOwner) {
			toast.error("Bearbeitung ist außerhalb der Einreichfrist nicht möglich.");
		} else {
			toast.error("Sie können nur Ihre eigenen Konzertanfragen bearbeiten.");
		}
		return;
	}

	// Only handle clicks on actual bookings, not on opening hours events
	if (!booking) {
		return;
	}

	viewBooking(booking);
}

function handleDatesChange(dateInfo) {
	// FullCalendar will automatically call events function when date range changes
	// No need to emit date-range-change anymore

	// Update the current calendar date for our reactive query
	// Use the same logic as the calendar configs
	if (props.mode === "multi-room") {
		// Multi-room uses fetchInfo.end
		const newDate = dateInfo.end.toISOString().split("T")[0];
		currentCalendarDate.value = newDate;
	} else {
		// Single-room uses fetchInfo.start for the date range
		const newDate = dateInfo.start.toISOString().split("T")[0];
		currentCalendarDate.value = newDate;
	}
}

function handleEventDrop(dropInfo) {
	const { event, revert } = dropInfo;
	const booking = event.extendedProps.booking;

	// Revert the visual change until user confirms
	revert();

	// Create updated booking with new times
	const updatedBooking = {
		...booking,
		startTime: event.start.toISOString(),
		endTime: event.end.toISOString(),
	};

	// Use booking actions to edit the booking (will handle series choice automatically)
	const prefillData = {
		...booking,
		startTime: event.start.toISOString(),
		endTime: event.end.toISOString(),
		roomId: booking.roomId || props.roomId,
		studentId: booking.student?.id || null,
		employeeId: booking.employee?.id || null,
		bookingTypeId: booking.bookingTypeId || null,
		pattern: booking.series?.pattern || "",
		rrulePattern: booking.series?.pattern || "",
		cancellations: booking.series?.cancellations || [],
		cancelledDates: booking.series?.cancellations || [],
	};

	editBooking(updatedBooking, prefillData, true); // true indicates this is a drag/drop operation
}

function handleEventResize(resizeInfo) {
	const { event, revert } = resizeInfo;
	const booking = event.extendedProps.booking;

	// Revert the visual change until user confirms
	revert();

	// Create updated booking with new times
	const updatedBooking = {
		...booking,
		startTime: event.start.toISOString(),
		endTime: event.end.toISOString(),
	};

	// Use booking actions to edit the booking (will handle series choice automatically)
	const prefillData = {
		...booking,
		startTime: event.start.toISOString(),
		endTime: event.end.toISOString(),
		roomId: booking.roomId || props.roomId,
		studentId: booking.student?.id || null,
		employeeId: booking.employee?.id || null,
		bookingTypeId: booking.bookingTypeId || null,
		pattern: booking.series?.pattern || "",
		rrulePattern: booking.series?.pattern || "",
		cancellations: booking.series?.cancellations || [],
		cancelledDates: booking.series?.cancellations || [],
	};

	editBooking(updatedBooking, prefillData, true); // true indicates this is a drag/drop operation
}

// Filter handling functions
function handleRemoveFilter(filter) {
	if (filter.id === "multi-equipment") {
		// Clear all equipment selections
		const multiEquipmentFilter =
			calendarFilters.filterManager.getFilter("multi-equipment");
		if (multiEquipmentFilter) {
			multiEquipmentFilter.clearSelection();
			calendarFilters.filterManager._notifyChange();
		}
	} else if (filter.id === "multi-room-types") {
		// Clear all room type selections
		const multiRoomTypeFilter =
			calendarFilters.filterManager.getFilter("multi-room-types");
		if (multiRoomTypeFilter) {
			multiRoomTypeFilter.clearSelection();
			calendarFilters.filterManager._notifyChange();
		}
	} else if (filter.id === "seat-count") {
		// Clear seat count filter completely
		const seatCountFilter =
			calendarFilters.filterManager.getFilter("seat-count");
		if (seatCountFilter) {
			seatCountFilter.clearSelection();
			calendarFilters.filterManager._notifyChange();
		}
	} else if (filter.id === "usage-type") {
		// Clear usage type selection
		const usageTypeFilter =
			calendarFilters.filterManager.getFilter("usage-type");
		if (usageTypeFilter) {
			usageTypeFilter.clearSelection();
			calendarFilters.filterManager._notifyChange();
		}
	} else if (filter.id === "person-filter") {
		// Clear person selection
		const personFilter =
			calendarFilters.filterManager.getFilter("person-filter");
		if (personFilter) {
			personFilter.clearSelection();
			calendarFilters.filterManager._notifyChange();
		}
	} else if (filter.id === "room-search") {
		// Clear room search query
		const roomSearchFilter =
			calendarFilters.filterManager.getFilter("room-search");
		if (roomSearchFilter) {
			roomSearchFilter.clearSearch();
			calendarFilters.filterManager._notifyChange();
		}
	} else {
		calendarFilters.toggleFilter(filter.id);
	}
}

// Calendar refresh function
const refetchEvents = () => {
	if (calendar.value) {
		const calendarApi = calendar.value.getApi();
		calendarApi.refetchEvents();
	}
};

// Set up reactive query to watch for booking updates and trigger calendar refresh
// We need to track the current calendar date, not just today's date
// Initialize with URL date if available, otherwise today
const calendarUrlParams = new URLSearchParams(window.location.search);
const initialDate =
	calendarUrlParams.get("date") || new Date().toISOString().split("T")[0];
const currentCalendarDate = ref(initialDate);

// Create reactive computed for query key that updates when date changes
const currentBookingQuery = computed(() => {
	if (props.mode === "single-room" && props.roomId) {
		return useBookingsByRoomQuery(props.roomId, {
			start: currentCalendarDate.value,
			end: currentCalendarDate.value,
		});
	} else {
		return useAllBookingsQuery(currentCalendarDate.value);
	}
});

// Create the actual query that will re-run when the computed changes
const bookingWatchQuery = useQuery(currentBookingQuery);

// Extract holidays from the query data (for multi-room mode)
const holidays = computed(() => {
	if (props.mode !== "multi-room") return {};
	return bookingWatchQuery.data?.value?.holidays || {};
});

// Watch for successful query data updates and trigger calendar refresh
watch(
	() => bookingWatchQuery.data?.value,
	(newData, oldData) => {
		// Only trigger refresh if data actually changed and we're not in the initial load
		if (
			newData &&
			oldData &&
			JSON.stringify(newData) !== JSON.stringify(oldData)
		) {
			refetchEvents();
		}
	},
	{ deep: true }
);

// Register calendar refresh callback when component mounts
if (registerCalendarRefresh) {
	registerCalendarRefresh(refetchEvents);
}

// Cleanup when component unmounts
onBeforeUnmount(() => {
	unsubscribeCalendarFilterRefresh?.();
	unsubscribeCalendarFilterRefresh = null;

	if (unregisterCalendarRefresh) {
		unregisterCalendarRefresh(refetchEvents);
	}
});

// Expose methods for parent component (if needed)
__expose({
	createBooking,
	editBooking,
	viewBooking,
	refetchEvents,
});

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Toaster), { "rich-colors": "" }),
    _createElementVNode("div", {
      class: _normalizeClass(["room-booking-calendar", {
      'dim-other-bookings':
        _unref(calendarFilters)?.isDimOtherBookingsActive?.(),
      'single-room-mode': props.mode === 'single-room',
      'multi-room-mode': props.mode === 'multi-room',
    }])
    }, [
      _renderSlot(_ctx.$slots, "toolbar", { filterSelector: filterSelectorSlotProps.value }),
      _createCommentVNode(" Calendar Filters "),
      (showInlineFilterSelector.value)
        ? (_openBlock(), _createBlock(FilterSelector, _mergeProps({ key: 0 }, filterSelectorProps.value, _toHandlers(filterSelectorEvents)), null, 16 /* FULL_PROPS */))
        : _createCommentVNode("v-if", true),
      _createCommentVNode(" Holiday Banner (only for multi-room mode) "),
      (props.mode === 'multi-room')
        ? (_openBlock(), _createBlock(HolidayBanner, {
            key: 1,
            holidays: holidays.value,
            "current-date": currentCalendarDate.value
          }, null, 8 /* PROPS */, ["holidays", "current-date"]))
        : _createCommentVNode("v-if", true),
      (_openBlock(), _createBlock(_unref(FullCalendar), {
        key: `calendar-${props.roomId || 'multi-room'}`,
        ref_key: "calendar",
        ref: calendar,
        options: _unref(calendarOptions)
      }, {
        resourceAreaHeaderContent: _withCtx((arg) => [
          (props.mode === 'multi-room' && _unref(calendarFilters))
            ? (_openBlock(), _createBlock(RoomSearchHeader, {
                key: 0,
                "search-query": _unref(calendarFilters).getRoomSearchQuery?.(),
                "onUpdate:search": handleRoomSearch
              }, null, 8 /* PROPS */, ["search-query"]))
            : (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(arg.text), 1 /* TEXT */))
        ]),
        resourceLabelContent: _withCtx((arg) => [
          _createElementVNode("b", null, [
            _createElementVNode("i", {
              class: _normalizeClass(_unref(generateResourceIconClass)(arg.resource))
            }, null, 2 /* CLASS */),
            _createElementVNode("a", {
              ref: (el) => setRoomLabelRef(arg.resource.id, el),
              href: "#",
              onClick: _withModifiers($event => (showRoomInfoPopover(arg.resource)), ["prevent"])
            }, _toDisplayString(arg.resource.title), 9 /* TEXT, PROPS */, _hoisted_2)
          ])
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["options"])),
      _createCommentVNode(" Calendar Date Picker "),
      _createVNode(CalendarDatePicker, {
        "is-visible": datePickerState.value.isVisible,
        "current-date": datePickerState.value.currentDate,
        onClose: closeDatePicker,
        onDateSelected: handleDateSelected
      }, null, 8 /* PROPS */, ["is-visible", "current-date"]),
      _createCommentVNode(" Room Info Popover "),
      _createVNode(RoomInfoPopover, {
        "is-open": roomPopoverState.value.isOpen,
        "room-data": roomPopoverState.value.roomData,
        "trigger-element": roomPopoverState.value.triggerElement,
        onClose: closeRoomInfoPopover
      }, null, 8 /* PROPS */, ["is-open", "room-data", "trigger-element"])
    ], 2 /* CLASS */)
  ], 64 /* STABLE_FRAGMENT */))
}
}

}